import * as Sentry from '@sentry/react';
import React, { ReactElement, ReactNode, useReducer } from 'react';
import { message, Space } from 'antd';

import { User } from '../../@types/authContext';
import { HttpClient } from '../../utils/HttpClient';
import { CLOSE_SESSION, GET_USER, LOGIN_ERROR, LOGIN_SUCCESS, REGISTER_ERROR, REGISTER_SUCCESS } from '../actions';
import AuthContext from './authContext';
import AuthReducer from './authReducer';

const AuthState = ({ children }: { children: ReactNode }): ReactElement => {
    const initialState = {
        logged: !!localStorage.getItem('logged'),
        user: {
            id: null,
            role: null,
            enabled: false,
            name: null,
            surname: null,
            email: null,
            permissions: null,
            groups: null,
        },
        message: null,
        loading: true,
        startSession: (): void => {},
        userlogged: (): void => {},
        closeSession: (): void => {},
        registerUser: (): void => {},
        googleAuth: (): void => {},
    };

    const [state, dispatch] = useReducer(AuthReducer, initialState);

    // Gets currently logged user
    async function userlogged(): Promise<void> {
        try {
            const user = await HttpClient.get('/users/self');
            dispatch({
                type: GET_USER,
                payload: {
                    id: user.data.message.id,
                    role: user.data.message.role,
                    enabled: user.data.message.enabled,
                    email: user.data.message.email,
                    name: user.data.message?.name,
                    surname: user.data.message?.surname,
                    permissions: user.data.message?.permissions,
                    groups: user.data.message?.groups,
                },
            });
        } catch (error) {
            dispatch({
                type: LOGIN_ERROR,
                payload: error.response?.data?.error?.reason || 'Error communicating with the API',
            });
        }
    }

    async function registerUser(data: User): Promise<void> {
        try {
            const response = await HttpClient.post('/users/register', data);
            if (response.data.ok) {
                message.success(
                    'User successfully registered! You should have a verification link on your email with instructions to validate your email',
                    0,
                );
                dispatch({
                    type: REGISTER_SUCCESS,
                    payload: response.data,
                });
            }
        } catch (error) {
            const alert = {
                mssg: error.message || 'Error communicating with the API',
                category: 'alert-error',
            };
            dispatch({ type: REGISTER_ERROR, payload: alert });
        }
    }

    async function googleAuth(googleCode: string): Promise<void> {
        try {
            const response = await HttpClient.post(`/google/auth`, { googleCode });
            if (response.data.ok) {
                dispatch({
                    type: LOGIN_SUCCESS,
                    payload: response.data,
                });
            }
        } catch (error) {
            const alert = {
                mssg: error.response?.data?.error?.reason || 'Error communicating with the API',
                category: 'alert-error',
            };

            dispatch({
                type: LOGIN_ERROR,
                payload: alert,
            });
        }
    }

    async function startSession(data: User): Promise<void> {
        try {
            const response = await HttpClient.post('/users/login', data);

            // eslint-disable-next-line no-undef
            if (PRODUCTION) {
                Sentry.setUser({ id: response.data.userId });
            }

            dispatch({
                type: LOGIN_SUCCESS,
                payload: response.data,
            });

            userlogged();
        } catch (error) {
            const alert = {
                mssg: error.message || 'Error communicating with the API',
                category: 'alert-error',
            };

            dispatch({
                type: LOGIN_ERROR,
                payload: alert,
            });
        }
    }

    function closeSession(): void {
        // eslint-disable-next-line no-undef
        if (PRODUCTION) {
            Sentry.configureScope((scope) => scope.setUser(null));
        }
        dispatch({
            type: CLOSE_SESSION,
        });
    }

    return (
        <AuthContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={{
                token: state.token!,
                logged: state.logged!,
                /* eslint-disable indent */
                user: state.user
                    ? {
                          id: state.user!.id!,
                          role: state.user!.role!,
                          enabled: state.user!.enabled!,
                          name: state.user!.name!,
                          surname: state.user!.surname!,
                          email: state.user!.email!,
                          permissions: state.user!.permissions!,
                          groups: state.user!.groups!,
                      }
                    : null,
                /* eslint-enable indent */
                message: state.message!,
                loading: state.loading,
                registerUser,
                startSession,
                userlogged,
                closeSession,
                googleAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
export default AuthState;
